/** @jsxImportSource @emotion/react */
'use client';

import React, { useId } from 'react';
import { ErrorText, Label } from './Label';
import { FieldRoot, HelpText } from './styled';
import type { FormFieldProps } from './types';

function mergeDescribedBy(
  ...ids: Array<string | undefined | null | false>
): string | undefined {
  const merged = ids.filter(Boolean).join(' ').trim();
  return merged || undefined;
}

export function FormField({
  label,
  labelWeight = 'bold',
  required = false,
  error,
  helpText,
  htmlFor,
  children,
  className,
  testId = 'form-field',
  css,
  style,
}: FormFieldProps) {
  const generatedId = useId();
  const fieldId = htmlFor ?? generatedId;
  const errorId = error ? `${fieldId}-error` : undefined;
  const helpId = helpText && !error ? `${fieldId}-help` : undefined;

  const child = React.isValidElement(children)
    ? (() => {
        const childProps = (children as React.ReactElement<any>).props ?? {};
        const existingDescribedBy =
          typeof childProps['aria-describedby'] === 'string'
            ? childProps['aria-describedby']
            : undefined;

        return React.cloneElement(children as React.ReactElement<any>, {
          id: childProps.id ?? fieldId,
          hasError: childProps.hasError ?? Boolean(error),
          'aria-describedby': mergeDescribedBy(
            existingDescribedBy,
            errorId,
            helpId
          ),
          'aria-invalid': Boolean(error) || childProps['aria-invalid'] || undefined,
          ...(required
            ? {
                required: childProps.required ?? true,
                'aria-required':
                  childProps['aria-required'] ?? true,
              }
            : {}),
        });
      })()
    : children;

  return (
    <FieldRoot
      className={className}
      css={css}
      style={style}
      data-testid={testId}
    >
      {label ? (
        <Label htmlFor={fieldId} weight={labelWeight} required={required}>
          {label}
        </Label>
      ) : null}
      {child}
      {error ? <ErrorText id={errorId}>{error}</ErrorText> : null}
      {!error && helpText ? <HelpText id={helpId}>{helpText}</HelpText> : null}
    </FieldRoot>
  );
}
